import { useState, useEffect, useRef } from 'react';

const INITIAL_MESSAGE = {
  id: 'welcome-msg',
  role: 'assistant',
  content: `👋 Hello! I am the official AI assistant for Sagveek Technologies. How may I help you today?`,
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  relatedQuestions: [],
};

function isGreeting(text = '') {
  if (!text || typeof text !== 'string') return false;
  const clean = text.trim().toLowerCase().replace(/[!.,?]+$/, '').trim();
  const greetingPattern = /^(h+i+|h+e+y+|h+e+l+o+|h+l+o+|h+l+w+|h+y+|greetings|namaste|namaskar|hola|good\s+(morning|afternoon|evening|day))(\s+(there|sagveek|assistant|bot|team|all|sir|madam))?$/i;
  return greetingPattern.test(clean);
}

const CLIENT_STOP_WORDS = new Set([
  'what', 'which', 'where', 'when', 'who', 'whom', 'whose', 'why', 'how',
  'does', 'do', 'did', 'done', 'doing', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'having', 'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might', 'must',
  'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'about', 'into', 'through',
  'here', 'there', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other', 'some', 'such',
  'you', 'your', 'yours', 'our', 'ours', 'their', 'his', 'her', 'its', 'this', 'that', 'these', 'those',
  'sagveek', 'technologies', 'pune', 'offer', 'offers', 'offered', 'provide', 'provides', 'provided',
  'tell', 'give', 'get', 'make', 'know', 'need', 'want', 'like', 'also', 'typical', 'available', 'options', 'details', 'seat', 'book', 'reserve'
]);

const CLIENT_SYNONYMS = {
  track: 'cours',
  tracks: 'cours',
  program: 'cours',
  programs: 'cours',
  classes: 'cours',
  trial: 'demo',
  sample: 'demo',
  tuition: 'fee',
  emi: 'instal',
  installment: 'instal',
  installments: 'instal',
  schedule: 'batch',
  schedules: 'batch',
  timing: 'batch',
  timings: 'batch',
};

function normalizeWord(w) {
  const lower = w.toLowerCase();
  if (CLIENT_SYNONYMS[lower]) return CLIENT_SYNONYMS[lower];
  const stemmed = lower.replace(/ies$/, 'y').replace(/s$/, '');
  return CLIENT_SYNONYMS[stemmed] || stemmed;
}

const CLIENT_SHORT_TECH_TERMS = new Set(['ai', 'ml', 'ui', 'ux', 'qa', 'it', 'ip', 'hr', 'js', 'db', 'ci', 'cd']);

function getWords(str = '') {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .split(/\s+/)
    .filter(w => (w.length > 2 || CLIENT_SHORT_TECH_TERMS.has(w)) && !CLIENT_STOP_WORDS.has(w))
    .map(normalizeWord);
}

function calculateSimilarity(q1, q2) {
  if (!q1 || !q2) return 0;
  const c1 = q1.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  const c2 = q2.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  if (c1 && c1 === c2) return 1;

  const s1 = new Set(getWords(q1));
  const s2 = new Set(getWords(q2));
  if (s1.size === 0 || s2.size === 0) return 0;
  let matches = 0;
  for (const w of s2) {
    if (s1.has(w)) matches++;
  }
  return matches / (s1.size + s2.size - matches);
}

function getCandidateText(c) {
  return typeof c === 'string' ? c.trim() : (c?.text || '').trim();
}

const CLIENT_QUESTION_BANK = {
  python: [
    'What projects and frameworks (Django/Flask) are taught in Python?',
    'What are the batch timings and how do I join a demo class?',
    '🎫 Raise a Ticket to book a free Python demo class'
  ],
  datascience: [
    'What machine learning and AI topics are covered in Data Science?',
    'What placement support and interview preparation do you provide?',
    '🎫 Raise a Ticket to book a Data Science demo class'
  ],
  mern: [
    'What full-stack projects will I build in the MERN course?',
    'What are the batch schedules for working professionals?',
    '🎫 Raise a Ticket to enroll or book a MERN demo class'
  ],
  java: [
    'What is covered in Spring Boot and microservices?',
    'What career roles can I apply for after Java Full Stack?',
    '🎫 Raise a Ticket to book a Java Full Stack demo class'
  ],
  devops: [
    'Which AWS services and Docker/Kubernetes tools are taught?',
    'What are the course fee and installment options?',
    '🎫 Raise a Ticket to book a Cloud & DevOps demo class'
  ],
  demo: [
    'What is covered in the free live demo class?',
    'What are the upcoming batch dates and timings?',
    '🎫 Raise a Ticket to reserve a free demo class seat'
  ],
  fee: [
    'What are the fee installment (EMI) options available?',
    'Are there early-bird discounts for upcoming batches?',
    '🎫 Raise a Ticket for class fee structure & EMI details'
  ],
  placement: [
    'How does Sagveek prepare students for mock interviews?',
    'Which hiring partner companies visit for campus drives?',
    '🎫 Raise a Ticket for career & placement counseling'
  ],
  ai: [
    'What custom AI chatbots and agent systems can you build?',
    'How does Sagveek connect internal knowledge bases with RAG?',
    '🎫 Raise a Ticket for AI & software development consultation'
  ],
  software: [
    'What custom web and mobile development services do you offer?',
    'What is your development process and delivery timeline?',
    '🎫 Raise a Ticket for a custom software development quote'
  ],
  cloud_service: [
    'What cloud architecture and migration audits do you provide?',
    'What is your development lifecycle and delivery timeline?',
    '🎫 Raise a Ticket for cloud & DevOps architecture support'
  ],
  pricing: [
    'What engagement models (fixed-price vs dedicated team) do you have?',
    'What is the typical cost framework for an AI proof-of-concept?',
    '🎫 Raise a Ticket to request a software project proposal'
  ],
  ticket: [
    'How do I submit an inquiry ticket for personal assistance?',
    'How can I check the status of my existing ticket?',
    'What is the expected turnaround time for support tickets?'
  ],
  general: [
    'What IT career training tracks does Sagveek offer?',
    'What custom enterprise software and AI solutions do you build?',
    '🎫 Raise a Ticket for class admission or software development'
  ]
};

const CLIENT_RAISE_TICKET_BY_POOL = {
  python: '🎫 Raise a Ticket to book a free Python demo class',
  datascience: '🎫 Raise a Ticket to book a Data Science demo class',
  mern: '🎫 Raise a Ticket to enroll or book a MERN demo class',
  java: '🎫 Raise a Ticket to book a Java Full Stack demo class',
  devops: '🎫 Raise a Ticket to book a Cloud & DevOps demo class',
  demo: '🎫 Raise a Ticket to reserve a free demo class seat',
  fee: '🎫 Raise a Ticket for class fee structure & EMI details',
  placement: '🎫 Raise a Ticket for career & placement counseling',
  ai: '🎫 Raise a Ticket for AI & software development consultation',
  software: '🎫 Raise a Ticket for a custom software development quote',
  cloud_service: '🎫 Raise a Ticket for cloud & DevOps architecture support',
  pricing: '🎫 Raise a Ticket to request a software project proposal',
};

function detectClientCategoryKey(query = '') {
  const q = (query || '').toLowerCase();
  const isEnterprise =
    /\b(enterprise|company|startup|agency|business|client|our project|our team|build a|build an|develop a|develop an|hire|architecture|consulting|migration|custom software|web app|mobile app)\b/i.test(q) &&
    !/\b(course|courses|learn|training|syllabus|curriculum|class|classes|batch|batches|student|students|tuition|placement|certificate|demo class)\b/i.test(q);

  if (isEnterprise) {
    if (q.includes('ai') || q.includes('chatbot') || q.includes('rag') || q.includes('llm')) return 'ai';
    if (q.includes('cloud') || q.includes('devops') || q.includes('aws') || q.includes('kubernetes')) return 'cloud_service';
    if (q.includes('pricing') || q.includes('quote') || q.includes('retainer')) return 'pricing';
    return 'software';
  }

  if (q.includes('python') || q.includes('django') || q.includes('flask')) return 'python';
  if (q.includes('data science') || q.includes('machine learning') || q.includes('ai/ml')) return 'datascience';
  if (q.includes('mern') || q.includes('react') || q.includes('full stack web')) return 'mern';
  if (q.includes('java') || q.includes('spring boot')) return 'java';
  if (q.includes('devops') || q.includes('docker') || q.includes('kubernetes')) return 'devops';
  if (q.includes('demo') || q.includes('trial')) return 'demo';
  if (q.includes('fee') || q.includes('cost') || q.includes('installment') || q.includes('emi')) return 'fee';
  if (q.includes('placement') || q.includes('job') || q.includes('interview')) return 'placement';
  if (q.includes('ai') || q.includes('chatbot') || q.includes('rag')) return 'ai';
  if (q.includes('software') || q.includes('app') || q.includes('develop') || q.includes('lifecycle') || q.includes('nda')) return 'software';
  if (q.includes('pricing') || q.includes('quote') || q.includes('retainer')) return 'pricing';
  if (q.includes('ticket') || q.includes('status')) return 'ticket';
  if (q.includes('course') || q.includes('training') || q.includes('track') || q.includes('class') || q.includes('batch') || q.includes('certificate') || q.includes('prerequisite')) return 'python';
  return 'general';
}

function filterAndBackfillClientQuestions(candidates = [], query = '', history = []) {
  const categoryKey = detectClientCategoryKey(query);
  const primaryPool = CLIENT_QUESTION_BANK[categoryKey] || CLIENT_QUESTION_BANK.general;
  const categoryTicketCta = CLIENT_RAISE_TICKET_BY_POOL[categoryKey] || null;

  const historyQueries = (history || []).map(h => (h.role === 'user' ? h.content : '')).filter(Boolean);
  const allUserQueries = [query, ...historyQueries].filter(Boolean);

  const isDuplicateOrAsked = (candText, currentSelected = []) => {
    if (!candText || candText.length < 6) return true;
    if (/raise a ticket/i.test(candText)) {
      return currentSelected.some(existing => /raise a ticket/i.test(existing));
    }
    if (allUserQueries.some(prevQ => calculateSimilarity(candText, prevQ) >= 0.40)) {
      return true;
    }
    if (currentSelected.some(existing => calculateSimilarity(candText, existing) >= 0.45)) {
      return true;
    }
    return false;
  };

  const results = [];

  for (const cand of candidates || []) {
    const text = getCandidateText(cand);
    if (!isDuplicateOrAsked(text, results)) {
      results.push(text);
    }
    if (results.length >= 3) break;
  }

  if (results.length < 3) {
    const allPools = [primaryPool, CLIENT_QUESTION_BANK.general, ...Object.values(CLIENT_QUESTION_BANK)];
    for (const pool of allPools) {
      for (const item of pool) {
        const text = getCandidateText(item);
        if (!isDuplicateOrAsked(text, results)) {
          results.push(text);
        }
        if (results.length >= 3) break;
      }
      if (results.length >= 3) break;
    }
  }

  const finalThree = results.slice(0, 3);
  if (categoryTicketCta && !finalThree.some(s => /raise a ticket/i.test(s))) {
    if (finalThree.length >= 3) {
      finalThree[2] = categoryTicketCta;
    } else {
      finalThree.push(categoryTicketCta);
    }
  }

  return finalThree;
}

export function useChat() {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('sagveek_chat_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (parsed.length === 1 && parsed[0].id === 'welcome-msg') {
            return [INITIAL_MESSAGE];
          }
          return parsed.map((m, idx) => {
            if (m.id === 'welcome-msg') {
              return { ...INITIAL_MESSAGE, timestamp: m.timestamp || INITIAL_MESSAGE.timestamp };
            }
            const prevMsg = idx > 0 ? parsed[idx - 1] : null;
            if (
              m.role === 'assistant' &&
              (m.content?.includes('I am the official AI assistant for Sagveek Technologies') ||
               /^(👋\s*)?hello!/i.test(m.content?.trim() || '') ||
               (prevMsg && isGreeting(prevMsg.content)))
            ) {
              return { ...m, relatedQuestions: [] };
            }
            if (m.role === 'assistant' && Array.isArray(m.relatedQuestions) && m.relatedQuestions.length > 0) {
              const priorHistory = parsed.slice(0, idx).filter(x => x.id !== 'welcome-msg');
              const triggerQuery = prevMsg?.role === 'user' ? prevMsg.content : '';
              return {
                ...m,
                relatedQuestions: filterAndBackfillClientQuestions(m.relatedQuestions, triggerQuery, priorHistory),
              };
            }
            return m;
          });
        }
      }
    } catch (e) {
      console.error('Failed to load chat history from localStorage', e);
    }
    return [INITIAL_MESSAGE];
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sagveek_chat_history', JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save chat history to localStorage', e);
    }
  }, [messages]);

  const clearHistory = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([INITIAL_MESSAGE]);
    setError(null);
    setIsLoading(false);
    localStorage.removeItem('sagveek_chat_history');
  };

  const addTicketConfirmation = (ticket) => {
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const ticketMessage = {
      id: 'ticket-' + Date.now(),
      role: 'assistant',
      content: `✅ **Support Ticket Created!**\n\nThank you, **${ticket.name}**. Your mobile number (**${ticket.phone || 'Verified'}**) has been verified via OTP and your ticket **#${ticket.id}** has been saved. Updates and a full response will be sent directly to **${ticket.email}**.`,
      ticket,
      timestamp: now,
      relatedQuestions: [
        `Track status of ticket #${ticket.id}`,
        'What is your support turnaround time?',
        'What services does Sagveek provide?',
      ],
    };
    setMessages((prev) => [...prev, ticketMessage]);
  };

  const sendMessage = async (userText) => {
    const trimmed = userText?.trim();
    if (!trimmed || isLoading) return;

    setError(null);
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const userMsg = {
      id: 'msg-' + Date.now(),
      role: 'user',
      content: trimmed,
      timestamp: now,
    };

    const assistantPlaceholderId = 'msg-' + (Date.now() + 1);
    const assistantPlaceholder = {
      id: assistantPlaceholderId,
      role: 'assistant',
      content: '',
      timestamp: now,
      isStreaming: true,
      relatedQuestions: [],
      agentEvents: [],
      multiAgentWorkflow: null,
    };

    // Full history for suggestion deduplication, and recent history for backend context
    const allHistoryForDedup = messages
      .filter((m) => m.id !== 'welcome-msg')
      .map((m) => ({ role: m.role, content: m.content }));

    const currentHistory = allHistoryForDedup.slice(-12);

    setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
    setIsLoading(true);

    abortControllerRef.current = new AbortController();
    let accumulatedText = '';

    try {
      // Direct call to /api/chat, proxied to the Express backend by CRA.
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ message: trimmed, history: currentHistory }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Server returned HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      accumulatedText = '';
      let streamBuffer = '';
      let receivedRelatedQuestions = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        streamBuffer += decoder.decode(value, { stream: true });
        const lines = streamBuffer.split(/\r?\n/);
        
        // Keep trailing incomplete line in buffer
        streamBuffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith('data:')) continue;
          
          const dataStr = trimmedLine.replace(/^data:\s*/, '').trim();

          if (dataStr === '[DONE]') {
            continue;
          }

          try {
            const parsed = JSON.parse(dataStr);
            if (parsed.text) {
              accumulatedText += parsed.text;
              const nextContent = accumulatedText;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? { ...msg, content: nextContent }
                    : msg
                )
              );
            } else if (parsed.agentEvent) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? {
                        ...msg,
                        agentEvents: [...(msg.agentEvents || []), parsed.agentEvent],
                      }
                    : msg
                )
              );
            } else if (parsed.multiAgentWorkflow) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? {
                        ...msg,
                        multiAgentWorkflow: parsed.multiAgentWorkflow,
                      }
                    : msg
                )
              );
            } else if (parsed.relatedQuestions) {
              const cleanRelated = filterAndBackfillClientQuestions(
                parsed.relatedQuestions,
                trimmed,
                allHistoryForDedup
              );
              receivedRelatedQuestions = cleanRelated;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? { ...msg, relatedQuestions: cleanRelated }
                    : msg
                )
              );
            } else if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e) {
            if (dataStr && !dataStr.startsWith('{')) {
              accumulatedText += dataStr;
              const nextContent = accumulatedText;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? { ...msg, content: nextContent }
                    : msg
                )
              );
            }
          }
        }
      }

      // Flush any trailing line left in streamBuffer
      if (streamBuffer.trim() && streamBuffer.trim().startsWith('data:')) {
        const remainingDataStr = streamBuffer.trim().replace(/^data:\s*/, '').trim();
        if (remainingDataStr && remainingDataStr !== '[DONE]') {
          try {
            const parsed = JSON.parse(remainingDataStr);
            if (parsed.text) {
              accumulatedText += parsed.text;
            }
          } catch {
            if (!remainingDataStr.startsWith('{')) {
              accumulatedText += remainingDataStr;
            }
          }
        }
      }

      // Mark streaming complete with deduplicated related questions
      const isGreetingMsg = isGreeting(trimmed);
      const finalRelated = isGreetingMsg
        ? []
        : filterAndBackfillClientQuestions(
            Array.isArray(receivedRelatedQuestions) ? receivedRelatedQuestions : [],
            trimmed,
            allHistoryForDedup
          );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantPlaceholderId
            ? {
                ...msg,
                isStreaming: false,
                relatedQuestions: finalRelated,
              }
            : msg
        )
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Chat request cancelled');
      } else {
        console.error('Chat stream error:', err);
        setError(err.message);
        const isGreetingMsg = isGreeting(trimmed);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantPlaceholderId
              ? {
                  ...msg,
                  content:
                    accumulatedText ||
                    msg.content ||
                    (isGreetingMsg
                      ? `👋 Hello! I am the official AI assistant for Sagveek Technologies. How may I help you today?`
                      : `⚠️ Connection interrupted. Please check your network or try again.`),
                  isStreaming: false,
                  relatedQuestions: [],
                }
              : msg
          )
        );
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearHistory,
    addTicketConfirmation,
  };
}
